// Product demo catalogue client — the in-app "how it works" demos.
// Subcontractor side: list the enabled demos for their audience, and log view/thumb events.
// Admin side: list demos with engagement counts, toggle/edit/reorder.
import { supabase } from '@/lib/supabaseClient';

// ── Subcontractor ────────────────────────────────────────────────────────────

/** Enabled demos for the subcontractor audience, in display order. */
export async function listDemosForSubcontractor() {
  try {
    const { data, error } = await supabase
      .from('product_demos')
      .select('id, key, title, blurb, file_path')
      .eq('enabled', true)
      .eq('audience', 'subcontractor')
      .order('sort_order', { ascending: true });
    if (error) return { ok: false, rows: [] };
    return { ok: true, rows: Array.isArray(data) ? data : [] };
  } catch { return { ok: false, rows: [] }; }
}

/**
 * Vaulted deck HTML for one demo, via the gated RPC (admins and demo
 * identities only — migration 20261223). Returns null when the row has no
 * vaulted HTML (the public walkthrough films), when access is refused, or on
 * any error — callers fall back to file_path.
 */
export async function fetchDemoHtml(key) {
  if (!key) return null;
  try {
    const { data, error } = await supabase.rpc('product_demo_html', { p_key: key });
    if (error) return null;
    return typeof data === 'string' && data.length > 0 ? data : null;
  } catch { return null; }
}

/** Log a 'view' | 'thumb_up' | 'thumb_down'. Best-effort — a failure never blocks the UI. */
export async function recordDemoEvent({ subcontractorId, demoKey, event }) {
  if (!subcontractorId || !demoKey || !event) return { ok: false };
  try {
    const { error } = await supabase
      .from('product_demo_events')
      .insert({ subcontractor_id: subcontractorId, demo_key: demoKey, event });
    return { ok: !error };
  } catch { return { ok: false }; }
}

// ── Admin ────────────────────────────────────────────────────────────────────

/** All demos (incl. disabled) with engagement counts, for the management panel. */
export async function adminListDemos() {
  try {
    const { data, error } = await supabase.rpc('admin_list_product_demos');
    if (error) return { ok: false, rows: [], reason: 'forbidden' };
    return { ok: true, rows: Array.isArray(data) ? data : [] };
  } catch { return { ok: false, rows: [] }; }
}

/** Patch a demo: { enabled?, title?, blurb?, sort_order? }. Admin-gated by RLS. */
export async function updateDemo(id, patch) {
  if (!id || !patch) return { ok: false };
  try {
    const { error } = await supabase
      .from('product_demos')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id);
    return { ok: !error, reason: error ? 'error' : undefined };
  } catch { return { ok: false, reason: 'error' }; }
}

/** Swap sort_order between two demos (reorder up/down). */
export async function swapDemoOrder(a, b) {
  const r1 = await updateDemo(a.id, { sort_order: b.sort_order });
  const r2 = await updateDemo(b.id, { sort_order: a.sort_order });
  return { ok: r1.ok && r2.ok };
}
